import { useRef, useState } from 'react';
import './SiteUnitCommandsPopup.scoped.scss';
import Button from '@components/_elements/Button/Button';
import storeConnector from '@store/storeConnector';
import { roundToTwoNonZero } from '@utils/mathHelpers';
import useAlias from '@src/hooks/useAlias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import './DCDCCommandsForm.scss';

interface DCDCCommandsFormProps {
  showConfPopup: (command: string, type: string) => void;
  setOverInput: (isOver: boolean) => void;
  powerValue?: number;
  minPower: number | undefined;
  maxPower: number | undefined;
  itcBufferValue?: number;
  rampRateValue?: number;
}

const DCDCCommandsForm = ({
  showConfPopup,
  setOverInput,
  powerValue,
  minPower,
  maxPower,
  itcBufferValue,
  rampRateValue,
}: DCDCCommandsFormProps) => {
  const alias = useAlias();

  // State for each field
  const [power, setPower] = useState<number | string>(powerValue || 0);
  const [itcBuffer, setItcBuffer] = useState<number | string>(
    itcBufferValue || 0,
  );
  const [rampRate, setRampRate] = useState<number | string>(rampRateValue || 0);

  const inputFirstRef = useRef<HTMLInputElement>(null);
  const inputSecondRef = useRef<HTMLInputElement>(null);
  const inputThirdRef = useRef<HTMLInputElement>(null);

  // Function to get error class based on conditions
  const getErrorClass = (
    value: number | string,
    allowedMin?: number,
    allowedMax?: number,
  ): string => {
    // Treat empty/undefined input as invalid
    if (value === '' || value == null) {
      return 'error';
    }

    const v = Number(value);

    if (!Number.isFinite(v)) {
      return 'error';
    }

    // Check against min limit if provided
    if (allowedMin != null && Number.isFinite(allowedMin) && v < allowedMin) {
      return 'error';
    }

    // Check against max limit if provided
    if (allowedMax != null && Number.isFinite(allowedMax) && v > allowedMax) {
      return 'error';
    }

    return '';
  };

  const sendPowerCommand = () => {
    const commandString = `Power::${power}`;
    showConfPopup(commandString, 'value');
  };

  const sendITCBufferCommand = () => {
    const commandString = `SetITCBuffer::${itcBuffer}`;
    showConfPopup(commandString, 'value');
  };

  const sendRampRateCommand = () => {
    const commandString = `SetDCDCRampRate::${rampRate}`;
    showConfPopup(commandString, 'value');
  };

  return (
    <>
      <div className='popup-row power-command-form-container'>
        <div className='bold vertical-margin'>{alias('DCDC Commands')}</div>
        <div className='popup-table'>
          <div className='popup-table-col'>
            <div>{alias('Power (kW):')}</div>
            <div className='range-lable' />
            <div>{alias('ITC Buffer (kW):')}</div>
            <div className='range-lable' />
            <div>{alias('Ramp Rate (%/s):')}</div>
            <div className='range-lable' />
          </div>
          <div className='popup-table-col'>
            {/* Power (kW) */}
            <div className='split-conv-input'>
              <div className='split-conv-input-col'>
                <input
                  name='power-input'
                  ref={inputFirstRef}
                  className={
                    `comand-popup-inp ` +
                    getErrorClass(power, minPower, maxPower)
                  }
                  type='number'
                  step='0.1'
                  {...(typeof maxPower === 'number' ? { max: maxPower } : {})}
                  {...(typeof minPower === 'number' ? { min: minPower } : {})}
                  value={power}
                  onMouseEnter={() => setOverInput(true)}
                  onMouseLeave={() => setOverInput(false)}
                  onChange={(e) => setPower(e.target.value)}
                />
                <div className='range-lable'>
                  {minPower != null && maxPower != null ? (
                    <>
                      {alias('Range from')} {roundToTwoNonZero(minPower)}{' '}
                      {alias('to')} {roundToTwoNonZero(maxPower)}
                    </>
                  ) : minPower != null ? (
                    <>
                      {alias('Must be')} ≥ {roundToTwoNonZero(minPower)}
                    </>
                  ) : maxPower != null ? (
                    <>
                      {alias('Must be')} ≤ {roundToTwoNonZero(maxPower)}
                    </>
                  ) : null}
                </div>
              </div>
              <div className='split-conv-input-col'>kW</div>
              <div className='split-conv-input-col'>
                <Button
                  size='s'
                  disabled={
                    getErrorClass(power, minPower, maxPower) === 'error'
                  }
                  onClick={() => sendPowerCommand()}
                >
                  <FormattedAlias id='Send' />
                </Button>
              </div>
            </div>

            {/* ITC Buffer (kW) */}
            <div className='split-conv-input'>
              <div className='split-conv-input-col'>
                <input
                  name='itc-buffer-input'
                  ref={inputSecondRef}
                  className={
                    `comand-popup-inp ` + getErrorClass(itcBuffer, 0, undefined)
                  }
                  type='number'
                  step='0.1'
                  value={itcBuffer}
                  onMouseEnter={() => setOverInput(true)}
                  onMouseLeave={() => setOverInput(false)}
                  onChange={(e) => setItcBuffer(e.target.value)}
                />
                <div className='range-lable'>{alias('Must be')} ≥ 0</div>
              </div>
              <div className='split-conv-input-col'>kW</div>
              <div className='split-conv-input-col'>
                <Button
                  size='s'
                  disabled={getErrorClass(itcBuffer, 0, undefined) === 'error'}
                  onClick={() => sendITCBufferCommand()}
                >
                  <FormattedAlias id='Send' />
                </Button>
              </div>
            </div>

            {/* Ramp Rate (%/s) */}
            <div className='split-conv-input'>
              <div className='split-conv-input-col'>
                <input
                  name='ramp-rate-input'
                  ref={inputThirdRef}
                  className={
                    `comand-popup-inp ` + getErrorClass(rampRate, 0, 100)
                  }
                  type='number'
                  step='0.1'
                  value={rampRate}
                  onMouseEnter={() => setOverInput(true)}
                  onMouseLeave={() => setOverInput(false)}
                  onChange={(e) => setRampRate(e.target.value)}
                />
                <div className='range-lable'>
                  {alias('Range from')} 0 {alias('to')} 100
                </div>
              </div>
              <div className='split-conv-input-col'>%/s</div>
              <div className='split-conv-input-col'>
                <Button
                  size='s'
                  disabled={getErrorClass(rampRate, 0, 100) === 'error'}
                  onClick={() => sendRampRateCommand()}
                >
                  <FormattedAlias id='Send' />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error messages */}
      {getErrorClass(power, minPower, maxPower) === 'error' && (
        <div className='popup-row warn-alert'>
          {minPower != null && maxPower != null ? (
            <FormattedAlias
              id='The value of power must be between {min} and {max}'
              values={{
                min: roundToTwoNonZero(minPower),
                max: roundToTwoNonZero(maxPower),
              }}
            />
          ) : minPower != null ? (
            <FormattedAlias
              id='Power must be greater than or equal to {min}'
              values={{ min: roundToTwoNonZero(minPower) }}
            />
          ) : maxPower != null ? (
            <FormattedAlias
              id='Power must be less than or equal to {max}'
              values={{ max: roundToTwoNonZero(maxPower) }}
            />
          ) : (
            <FormattedAlias id='Please enter a valid power value' />
          )}
        </div>
      )}
      {getErrorClass(itcBuffer, 0, undefined) === 'error' && (
        <div className='popup-row warn-alert'>
          <FormattedAlias id='ITC Buffer must be greater than or equal to 0' />
        </div>
      )}
      {getErrorClass(rampRate, 0, 100) === 'error' && (
        <div className='popup-row warn-alert'>
          <FormattedAlias id='Ramp Rate must be between 0 and 100' />
        </div>
      )}
    </>
  );
};

export default storeConnector(DCDCCommandsForm, {
  config: ['siteMeta'],
});
